import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import { createJobMutation } from 'api/mutations/jobs';
import { addNegativeToast, addPositiveToast, errorIdentifier } from 'modules/toastContainer';
import { useJobManagerTranslations } from './useTranslations';
import { ExportJobArgs } from '../types';

type CallbackFn = () => void;

type CreateJob = {
  showSuccessToaster?: boolean;
  /** Do the same action whether the operation is a success or not */
  onComplete?: CallbackFn;
  onSuccess?: CallbackFn;
  onError?: CallbackFn;
};

const source = 'useCreateJobs';

export const useCreateJobs = ({
  showSuccessToaster = true,
  onComplete,
  onSuccess,
  onError,
}: CreateJob = {}): [(args: ExportJobArgs) => Promise<{ isSuccess: boolean }>, boolean] => {
  const { t } = useJobManagerTranslations();
  const dispatch = useDispatch();
  const onSuccessCallback = onSuccess || onComplete;
  const onErrorCallback = onError || onComplete;
  const errorMessage = `Failed to create job.`;
  const errorToasterMessage = t('errorMessage.createJob');
  const successToasterMessage = t('successMessage.createJob');

  const errorHandler = (error: unknown) => {
    if (onErrorCallback) onErrorCallback();
    dispatch(
      addNegativeToast(
        errorToasterMessage,
        errorIdentifier({
          errorMessage,
          source,
          error,
        }),
      ),
    );
  };

  const [createJob, { loading }] = useMutation(createJobMutation, {
    onError: errorHandler,
  });

  const handleCreateJob = async (exportArgs: ExportJobArgs) => {
    let isSuccess = true;
    const { data } = await createJob({
      variables: exportArgs,
    });

    if (data?.createExportJob?.id) {
      if (onSuccessCallback) onSuccessCallback();
      if (showSuccessToaster) {
        dispatch(addPositiveToast(successToasterMessage));
      }
    }

    if (!data) {
      isSuccess = false;
    }

    return { isSuccess };
  };

  return [handleCreateJob, loading];
};
